<template>
  <div class="investment-container">
    <el-card :body-style="{ paddingBottom: 0 }" class="mb-20">
      <bootstrap-form @confirm="onConfirmBootstrap" @reset="onResetConfig" @add-creative="onAddCreative" @template="onReadTemplate" />
    </el-card>
    <el-card>
      <main-view
        v-if="form_fields"
        ref="main-view-ref"
        :is-template="template_config.is_template"
        :is-add-creative="add_creative_config.is_add_creative"
        :add-creative-list="add_creative_config.table_selection"
        :form-fields="form_fields"
        @reset="onResetConfig(false)"
        @addCreative="onAddCreative(add_creative_config.init_config)"
      />
      <main-skeleton v-else />
    </el-card>
    <el-drawer
      :visible.sync="template_config.is_show"
      :append-to-body="true"
      :modal-append-to-body="true"
    >
      <span slot="title" class="font-16">{{ template_config.title }}</span>
      <template-list v-if="template_config.is_show" class="p-20" :init-config="template_config.init_config" @confirm="applicationTemplate" />
    </el-drawer>
    <el-dialog
      :visible.sync="add_creative_config.is_show"
      :title="add_creative_config.title"
      width="1050px"
    >
      <add-creative-list v-if="add_creative_config.is_show" ref="add-creative-list-ref" :init-config="add_creative_config.init_config" :table-selection="add_creative_config.table_selection" />
      <div slot="footer">
        <div class="w-full flex items-center justify-center">
          <el-button type="primary" @click="onAddCreativeConfirm">确定</el-button>
          <el-button @click="onAddCreativeCancel">取消</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FormContext from '@/views/fusion/investment/intelligence-create/strategy/FormContext';
import { DataSourceContext } from '@/views/fusion/investment/intelligence-create/controller';
import { MATERIAL_MODULE_KEY, TITLE_MODULE_KEY } from '@/views/fusion/investment/intelligence-create/strategy/constants';
import { executeFormFieldsEventListener, modulesCarousel, deepMergeTemplate } from '@/views/fusion/investment/intelligence-create/tools';
import { CHANNEL_INTERFACE_TYPE } from '@/enums';
import { hasOwnProperty } from '@/utils';

export default {
  components: {
    'bootstrap-form': () => import('@/views/fusion/investment/intelligence-create/components/bootstrap/index.vue'),
    'main-view': () => import('@/views/fusion/investment/intelligence-create/components/main-view/index.vue'),
    'main-skeleton': () => import('@/views/fusion/investment/intelligence-create/components/skeleton/index.vue'),
    'template-list': () => import('@/views/fusion/investment/intelligence-create/components/template/index.vue'),
    'add-creative-list': () => import('@/views/fusion/investment/intelligence-create/components/add-creative/index.vue'),
  },
  provide() {
    return {
      sharing_data: {
        getFormContext: () => this.form_context,
        getFormFields: () => this.form_fields,
      },
    };
  },
  data() {
    return {
      form_context: null,
      form_fields: null,
      // 模板配置
      template_config: {
        is_show: false,
        is_template: false,
        title: '模板列表',
        template_data: '',
        init_config: {},
      },
      // 添加创意配置
      add_creative_config: {
        is_show: false,
        is_add_creative: false,
        title: '选择广告',
        table_selection: [],
        init_config: {},
      },
    };
  },
  computed: {
    is_reset_status() {
      return this.form_context === null && this.form_fields === null;
    },
  },
  methods: {
    /**
     * 生成渠道配置数据
     * @returns {void}
     */
    async generateChannelConfig({ advertiser_options, init_ops }) {
      const { delivery_form } = this.form_fields.campaign_config.config;
      const { fusion__channel_code } = delivery_form.config;
      const { fusion__channel_codes } = fusion__channel_code.children;
      const findLabel = (advertiser_id) => advertiser_options.find((item) => item.value === advertiser_id)?.label || '';
      fusion__channel_codes.value = init_ops.advertiser_ids.map(advertiser_id => {
        return {
          label: findLabel(advertiser_id),
          advertiser_id,
          list: [],
        };
      });
    },
    /**
     * 搭建广告
     * @param {Object} init_ops 初始化配置
     * @param {string} init_ops.channel_interface 渠道标识
     * @param {number} init_ops.advertiser_ids 广告主账户组成的数组
     * @param {string} init_ops.ad_type 广告类型
     * @param {Array<Object>} advertiser_options 广告主账户数据源选项
     */
    async onConfirmBootstrap({ init_ops, advertiser_options }) {
      const loading = this.$loading({ lock: false, text: '正 在 搭 建 广 告...', spinner: 'el-icon-loading', customClass: 'fusion-loading-mask' });
      try {
        const dataContext = new DataSourceContext({ init_ops });
        const dataController = dataContext.getDataSourceController();
        const channel_config = await dataController.getPlanConfig();
        this.form_context = new FormContext({ init_ops, channel_config, dataController });
        this.form_fields = await this.form_context.getFormFields();
        await this.generateChannelConfig({ advertiser_options, init_ops });
        await this.form_context.strategy.initChannelInterfaceConfig({ form_fields: this.form_fields, init_ops });
      } catch (error) {
        console.error(error);
        this.$message.error(`搭建广告失败：${JSON.stringify(error)}`);
        this.onResetConfig(false);
      } finally {
        loading.close();
      }
    },

    /**
     * 重置配置
     * @param {boolean} [show_message=true] 是否显示提示信息。默认显示
     * @returns {void}
     */
    onResetConfig(show_message = true) {
      this.form_context = null;
      this.form_fields = null;
      this.template_config.is_template = false;
      this.add_creative_config.is_add_creative = false;
      this.add_creative_config.table_selection = [];
      show_message && this.$message.success('重置成功！');
    },

    /**
     * 添加创意
     * @param {Object} init_config 初始化数据
     * @param {string} init_config.init_ops 初始化配置
     * @param {Object[]} init_config.advertiser_options 广告主账户数据源选项
     * @returns {void}
     */
    onAddCreative(init_config) {
      const { init_ops: { advertiser_ids }} = init_config;
      if (advertiser_ids.length > 1) {
        this.$message.warning('添加创意不允许选择多个广告主账户');
        return;
      }
      Object.assign(this.add_creative_config, { is_show: true, init_config });
    },

    async onAddCreativeConfirm() {
      const table_selection = this.$refs['add-creative-list-ref'].getTableSelection();
      if (table_selection.length === 0) {
        this.$message.warning('请选择广告');
        return;
      }

      /**
       * 二次编辑时，新选项与当前选项类型是否一致
       * @param current_table_selection 当前选项
       * @param table_selection 新选项
       * @returns {boolean}
       */
      const isSameTableSelection = (current_table_selection, table_selection) => {
        if (current_table_selection.length === 0) {
          return false;
        }

        const isArraysEqual = (arr1 = [], arr2 = []) => {
          if (arr1.length !== arr2.length) {
            return false;
          }

          const a1 = JSON.parse(JSON.stringify(arr1));
          const a2 = JSON.parse(JSON.stringify(arr2));

          return a1.sort().toString() === a2.sort().toString();
        };

        const { init_ops: { channel_interface }} = this.add_creative_config.init_config;

        const strategy = {
          [CHANNEL_INTERFACE_TYPE.GDT]: () => {
            const is_delivery_platform_type_same = current_table_selection[0].delivery_platform_type === table_selection[0].delivery_platform_type;
            const is_marketing_sub_goal_same = current_table_selection[0].gdt_params.marketing_sub_goal === table_selection[0].gdt_params.marketing_sub_goal;
            const is_site_set_type_same = current_table_selection[0].site_set_type === table_selection[0].site_set_type;
            const is_site_sets_same = isArraysEqual(current_table_selection[0].site_sets, table_selection[0].site_sets);

            const is_valid = [is_delivery_platform_type_same, is_marketing_sub_goal_same, is_site_set_type_same, is_site_sets_same].every(e => e);
            return is_valid;
          },
          [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
            const is_delivery_platform_type_same = current_table_selection[0].delivery_platform_type === table_selection[0].delivery_platform_type;
            const is_app_promotion_type_same = current_table_selection[0].jrtt_params.app_promotion_type === table_selection[0].jrtt_params.app_promotion_type;
            const is_site_set_type_same = current_table_selection[0].site_set_type === table_selection[0].site_set_type;
            const is_site_sets_same = isArraysEqual(current_table_selection[0].site_sets, table_selection[0].site_sets);
            const is_price_type_same = current_table_selection[0].price_type === table_selection[0].price_type;
            const is_price_strategy_same = current_table_selection[0].price_strategy === table_selection[0].price_strategy;
            const is_marketing_goal_same = current_table_selection[0].jrtt_params.marketing_goal === table_selection[0].jrtt_params.marketing_goal;
            const is_delivery_mode_same = current_table_selection[0].jrtt_params.delivery_mode === table_selection[0].jrtt_params.delivery_mode;
            const is_star_task_switch_same = current_table_selection[0].jrtt_params.star_task_switch === table_selection[0].jrtt_params.star_task_switch;
            const is_star_auto_material_addition_switch_same = current_table_selection[0].jrtt_params.star_auto_material_addition_switch === table_selection[0].jrtt_params.star_auto_material_addition_switch;
            const is_convert_goal_same = current_table_selection[0].jrtt_params.convert_goal === table_selection[0].jrtt_params.convert_goal;
            const is_deep_convert_goal_same = current_table_selection[0].jrtt_params.deep_convert_goal === table_selection[0].jrtt_params.deep_convert_goal;
            const is_deep_bid_type_same = current_table_selection[0].jrtt_params.deep_bid_type === table_selection[0].jrtt_params.deep_bid_type;
            const is_budget_optimize_switch_same = current_table_selection[0].jrtt_params.budget_optimize_switch === table_selection[0].jrtt_params.budget_optimize_switch;

            const is_valid = [
              is_delivery_platform_type_same,
              is_app_promotion_type_same,
              is_site_set_type_same,
              is_site_sets_same,
              is_price_type_same,
              is_price_strategy_same,
              is_marketing_goal_same,
              is_delivery_mode_same,
              is_star_task_switch_same,
              is_star_auto_material_addition_switch_same,
              is_convert_goal_same,
              is_deep_convert_goal_same,
              is_deep_bid_type_same,
              is_budget_optimize_switch_same,
            ].every(e => e);
            return is_valid;
          },
          [CHANNEL_INTERFACE_TYPE.BILIBILI]: () => {
            const is_campaign_same = current_table_selection[0].id === table_selection[0].id;

            return is_campaign_same;
          },
          [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
            const is_delivery_platform_type_same = current_table_selection[0].delivery_platform_type === table_selection[0].delivery_platform_type;
            const is_site_set_type_same = current_table_selection[0].site_set_type === table_selection[0].site_set_type;
            const is_site_sets_same = isArraysEqual(current_table_selection[0].site_sets, table_selection[0].site_sets);
            const is_price_same = hasOwnProperty(current_table_selection[0], 'price') === hasOwnProperty(table_selection[0], 'price');
            const is_ocpc_bid_same = hasOwnProperty(current_table_selection[0].baidu_params, 'ocpc_bid') === hasOwnProperty(table_selection[0].baidu_params, 'ocpc_bid');
            const is_deep_ocpc_bid_same = hasOwnProperty(current_table_selection[0].baidu_params, 'deep_ocpc_bid') === hasOwnProperty(table_selection[0].baidu_params, 'deep_ocpc_bid');
            const is_roi_ratio_same = hasOwnProperty(current_table_selection[0].baidu_params, 'roi_ratio') === hasOwnProperty(table_selection[0].baidu_params, 'roi_ratio');

            const is_valid = [is_delivery_platform_type_same, is_site_set_type_same, is_site_sets_same, is_price_same, is_ocpc_bid_same, is_deep_ocpc_bid_same, is_roi_ratio_same].every(e => e);
            return is_valid;
          },
        };

        return strategy[channel_interface]();
      };

      Object.assign(this.add_creative_config, { is_add_creative: true });
      if (this.is_reset_status) {
        await this.onConfirmBootstrap(this.add_creative_config.init_config);
      }
      if (!isSameTableSelection(this.add_creative_config.table_selection, table_selection)) {
        setTimeout(() => {
          this.$nextTick(() => {
            this.renderDynamicComponent(table_selection);
          });
        }, 0);
      }
      Object.assign(this.add_creative_config, { table_selection });
      this.onAddCreativeCancel();
    },

    /**
     * 取消添加创意弹窗
     * @returns {void}
     */
    onAddCreativeCancel() {
      Object.assign(this.add_creative_config, { is_show: false });
    },

    editCreative() {
      this.onAddCreative(this.add_creative_config.init_config);
    },

    /**
     * 读取模板
     * @param {Object} init_config 初始化数据
     * @param {string} init_config.init_ops 初始化配置
     * @param {Object[]} init_config.advertiser_options 广告主账户数据源选项
     * @returns {void}
     */
    async onReadTemplate(init_config) {
      Object.assign(this.template_config, {
        is_show: true,
        init_config,
      });
    },

    /**
     * 应用模板
     * @param {Object} template 模板数据
     * @param {Object} init_config 初始化数据
     * @returns {void}
     */
    async applicationTemplate(template, init_config) {
      this.onResetConfig(false);
      Object.assign(this.template_config, { is_show: false, template_data: template.template_data, is_template: true });
      await this.onConfirmBootstrap(init_config);
      const template_data = JSON.parse(template.template_data);
      this.form_fields = deepMergeTemplate([this.form_fields, template_data]);
      const { strategy, config } = this.form_context;
      await strategy.addEvent(this.form_fields, config.dataController);
      modulesCarousel(this.form_fields, ({ form_config }) => executeFormFieldsEventListener(form_config.config));
      this.$nextTick(() => {
        this.initFormModelData();
      });
    },

    /**
     * 初始化主视图表单模型
     * @returns {void}
     */
    initFormModelData() {
      const strategy = {
        [MATERIAL_MODULE_KEY]: (field_key) => {
          return this.form_fields[field_key].config.material_form.config.material.value;
        },
        [TITLE_MODULE_KEY]: (field_key) => {
          return this.form_fields[field_key].selection_config.list;
        },
      };
      Object.keys(JSON.parse(this.template_config.template_data)).forEach(key => {
        if (strategy?.[key]) {
          Object.assign(this.$refs['main-view-ref'].form_model, {
            [key]: strategy[key](key),
          });
        }
      });
    },

    /**
     * 渲染动态创意
     * @param {Object[]} table_selection 添加创意选中数据
     * @returns {void}
     */
    renderDynamicComponent(table_selection) {
      const campaign = table_selection[0];
      const { dataController } = this.form_context.config;
      const strategy = {
        [CHANNEL_INTERFACE_TYPE.GDT]: () => {
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    delivery_platform_type,
                    marketing_sub_goal,
                  },
                },
                site_form: {
                  config: {
                    site_set_type,
                    site_sets,
                  },
                },
              },
            },
          } = this.form_fields;
          delivery_platform_type.value = campaign.delivery_platform_type;
          marketing_sub_goal.value = campaign.gdt_params.marketing_sub_goal;
          marketing_sub_goal.backup_value = campaign.gdt_params.marketing_sub_goal;
          site_set_type.value = campaign.site_set_type;
          site_sets.value = campaign.site_sets;

          return {
            raw_ref_form_model: {
              'campaign_config.site_form': {
                site_set_type: campaign.site_set_type,
                site_sets: campaign.site_sets,
              },
              'campaign_config.delivery_form': {
                delivery_platform_type: campaign.delivery_platform_type,
                marketing_sub_goal: campaign.gdt_params.marketing_sub_goal,
              },
            },
            raw_form_model: {},
            form_assign_data: {},
          };
        },
        [CHANNEL_INTERFACE_TYPE.JRTT]: () => {
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    fusion__channel_code: {
                      children: {
                        fusion__channel_codes,
                      },
                    },
                    delivery_platform_type,
                    delivery_content_id,
                    marketing_goal,
                    delivery_mode,
                    star_task_switch,
                    star_tasks,
                    star_auto_material_addition_switch,
                  },
                },
                budget_and_bid_form: {
                  config: {
                    price_type,
                    price_strategy,
                    deep_convert_type,
                    deep_bid_type,
                    budget_optimize_switch,
                  },
                },
              },
            },
          } = this.form_fields;

          const { channel_config: { campaign: { campaign_name_separator }}} = this.form_context.config;
          const { advertiser_options, init_ops: { advertiser_ids }} = this.add_creative_config.init_config;
          const channel_codes = advertiser_ids.map((advertiser_id) => {
            return {
              advertiser_id,
              label: advertiser_options.find(({ value }) => advertiser_id === value)?.label || '',
              list: table_selection.map(({ name }) => {
                return {
                  alias: name.split(campaign_name_separator)[1],
                };
              }),
            };
          });
          const delivery_content_value = {
            result_map: {
              [advertiser_ids[0]]: {
                options: [{
                  label: campaign.delivery_content.name,
                  value: `${campaign.delivery_content.id}#${campaign.delivery_content.name}`,
                  jrtt_params: {
                    micro_app_id: campaign.delivery_content.micro_app_id,
                    micro_app_instance_id: campaign.delivery_content.micro_app_instance_id,
                  }}],
                value: `${campaign.delivery_content.id}#${campaign.delivery_content.name}`,
              },
            },
          };

          const star_tasks_value = {};
          campaign.jrtt_params.star_task_id && advertiser_ids.forEach(advertiser_id => {
            Object.assign(star_tasks_value, {
              [advertiser_id]: [{ id: campaign.jrtt_params.star_task_id }],
            });
          });

          fusion__channel_codes.value = channel_codes;
          delivery_platform_type.value = campaign.delivery_platform_type;
          marketing_goal.value = campaign.jrtt_params.marketing_goal;
          price_type.value = campaign.price_type;
          price_strategy.value = campaign.price_strategy;
          delivery_mode.value = campaign.jrtt_params.delivery_mode;
          delivery_mode.backup_value = delivery_mode.value;
          star_task_switch.value = campaign.jrtt_params.star_task_switch;
          star_tasks.value = star_tasks_value;
          star_auto_material_addition_switch.value = campaign.jrtt_params.star_auto_material_addition_switch;
          deep_convert_type.value = campaign.jrtt_params.deep_convert_goal;
          deep_bid_type.value = campaign.jrtt_params.deep_bid_type;
          budget_optimize_switch.value = campaign.jrtt_params.budget_optimize_switch;
          delivery_content_id.value = delivery_content_value;
          delivery_content_id.backup_value = delivery_content_id.value;

          return {
            raw_ref_form_model: {
              'campaign_config.delivery_form': {
                fusion__channel_code: {
                  fusion__channel_codes: channel_codes,
                },
                delivery_platform_type: campaign.delivery_platform_type,
                marketing_goal: campaign.jrtt_params.marketing_goal,
                delivery_mode: campaign.jrtt_params.delivery_mode,
                delivery_content_id: delivery_content_value,
                star_task_switch: campaign.jrtt_params.star_task_switch,
                star_auto_material_addition_switch: campaign.jrtt_params.star_auto_material_addition_switch,
              },
              'campaign_config.budget_and_bid_form': {
                price_type: campaign.price_type,
                price_strategy: campaign.price_strategy,
                deep_convert_type: campaign.jrtt_params.deep_convert_goal,
                deep_bid_type: campaign.jrtt_params.deep_bid_type,
                budget_optimize_switch: campaign.jrtt_params.budget_optimize_switch,
              },
            },
            raw_form_model: {},
            form_assign_data: {},
          };
        },
        [CHANNEL_INTERFACE_TYPE.BILIBILI]: () => {},
        [CHANNEL_INTERFACE_TYPE.BAIDU]: () => {
          const {
            campaign_config: {
              config: {
                delivery_form: {
                  config: {
                    delivery_platform_type,
                  },
                },
                site_form: {
                  config: {
                    site_set_type,
                    site_sets,
                  },
                },
                budget_and_bid_form: {
                  config: {
                    price_strategy,
                    price,
                    ocpc_bid,
                    deep_ocpc_bid,
                    roi_ratio,
                  },
                },
              },
            },
          } = this.form_fields;
          delivery_platform_type.value = campaign.delivery_platform_type;
          delivery_platform_type.backup_value = campaign.delivery_platform_type;
          site_set_type.value = campaign.site_set_type;
          site_sets.value = campaign.site_sets;
          price_strategy.value = campaign.price_strategy;
          price.hidden = !hasOwnProperty(campaign, 'price');
          ocpc_bid.hidden = !hasOwnProperty(campaign.baidu_params, 'ocpc_bid');
          deep_ocpc_bid.hidden = !hasOwnProperty(campaign.baidu_params, 'deep_ocpc_bid');
          roi_ratio.hidden = !hasOwnProperty(campaign.baidu_params, 'roi_ratio');

          return {
            raw_ref_form_model: {
              'campaign_config.site_form': {
                site_set_type: campaign.site_set_type,
                site_sets: campaign.site_sets,
              },
              'campaign_config.delivery_form': {
                delivery_platform_type: campaign.delivery_platform_type,
              },
              'campaign_config.budget_and_bid_form': {
                price: campaign.price,
                ocpc_bid: campaign.baidu_params?.ocpc_bid,
                deep_ocpc_bid: campaign.baidu_params?.deep_ocpc_bid,
                roi_ratio: campaign.baidu_params?.roi_ratio,
              },
            },
            raw_form_model: {},
            form_assign_data: {},
          };
        },
      };
      const { init_ops: { channel_interface }} = this.add_creative_config.init_config;
      this.$refs['main-view-ref'].set_dynamic_component_params = {
        config: this.form_fields,
        controller: dataController,
        payload: strategy[channel_interface](),
      };
      this.$refs['main-view-ref'].setDynamicComponent();
    },
  },
};
</script>

<style lang="scss">
.investment-container {
  padding: 20px;
  background-color: #f8f8f8;
  // 打开【素材弹窗】会导致所有的横竖滚动条，在style层级上被添加上width: 0px，导致滚动条显示异常，原因未知，暂做以下特殊处理
  .el-scrollbar {
    .el-scrollbar__bar.is-horizontal {
      width: auto !important;
    }
    .el-scrollbar__bar.is-vertical {
      width: 6px !important;
    }
  }
}
</style>
